import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Refrigerator, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRe = /^[0-9+()\-\s]{7,20}$/;

const TIER_LABEL = {
  starter: 'Starter ($749/wk)',
  team: 'Team ($1,779/wk)',
  hq: 'HQ (custom quote)',
};

export default function FridgeLeadDialog({ open, onOpenChange, tier = 'starter' }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', headcount: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const reset = () => {
    setForm({ name: '', email: '', phone: '', company: '', headcount: '', message: '' });
    setErrors({});
    setDone(false);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!emailRe.test(form.email.trim())) e.email = 'Valid email required';
    if (!phoneRe.test(form.phone.trim())) e.phone = 'Valid phone required';
    if (!form.company.trim()) e.company = 'Company is required';
    if (!form.headcount || Number(form.headcount) < 1) e.headcount = 'Headcount required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/fridge`, {
        tier,
        contact: {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          company: form.company.trim(),
        },
        headcount: Number(form.headcount),
        message: form.message.trim() || null,
      }, { timeout: 15000 });
      setDone(true);
      toast.success('Request received! Our team will reach out within 1 business day.');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Something went wrong';
      toast.error(typeof msg === 'string' ? msg : 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-[#FBF8EF] border border-[#D6CFB8] sm:max-w-lg">
        {!done ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-9 h-9 rounded-full bg-[#245A40] grid place-items-center">
                  <Refrigerator className="w-4 h-4 text-[#E8C56B]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#245A40] font-bold">Fridge Program</div>
                  <div className="text-sm text-[#6F6A55]">Interested in: <span className="font-semibold text-[#1B2A22]">{TIER_LABEL[tier]}</span></div>
                </div>
              </div>
              <DialogTitle className="font-display text-2xl text-[#1B2A22]">Tell us about your team</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name*" error={errors.name}><Input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Full name" /></Field>
                <Field label="Company*" error={errors.company}><Input value={form.company} onChange={(e) => setField('company', e.target.value)} placeholder="Company name" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email*" error={errors.email}><Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="you@company.com" /></Field>
                <Field label="Phone*" error={errors.phone}><Input type="tel" value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="773-555-0123" /></Field>
              </div>
              <Field label="Team size (headcount)*" error={errors.headcount}>
                <Input type="number" min="1" value={form.headcount} onChange={(e) => setField('headcount', e.target.value)} placeholder="e.g. 40" />
              </Field>
              <Field label="Anything we should know?" hint="(optional)">
                <Textarea rows={3} value={form.message} onChange={(e) => setField('message', e.target.value)} placeholder="Office address, start date, dietary preferences, etc." />
              </Field>
            </div>

            <DialogFooter className="mt-2">
              <Button onClick={submit} disabled={submitting} className="btn-primary w-full h-11 rounded-full font-semibold disabled:opacity-60">
                {submitting ? 'Submitting…' : 'Send my request'}
              </Button>
            </DialogFooter>
            <p className="text-[11px] text-[#6F6A55] text-center">We’ll reach out within 1 business day. No spam — ever.</p>
          </>
        ) : (
          <div className="text-center py-6">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#245A40] grid place-items-center">
              <CheckCircle2 className="w-7 h-7 text-[#F6F2E8]" />
            </div>
            <DialogTitle className="font-display text-2xl text-[#1B2A22] mt-4">Request received</DialogTitle>
            <p className="text-sm text-[#3F4A42] mt-2 max-w-sm mx-auto">
              Thanks{form.name ? `, ${form.name.split(' ')[0]}` : ''}! Our team will reach out to <span className="font-semibold">{form.email}</span> within 1 business day to schedule a walkthrough.
            </p>
            <Button onClick={() => handleOpenChange(false)} className="btn-primary rounded-full h-11 px-8 mt-6 font-semibold">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-[#1B2A22] flex items-center gap-1">
        {label} {hint && <span className="text-[10px] font-normal text-[#6F6A55]">{hint}</span>}
      </Label>
      <div className="mt-1">{children}</div>
      {error && <div className="text-[11px] text-[#A4471A] mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</div>}
    </div>
  );
}
